import { api } from '../utils';

export interface EventItem {
  id: string;
  name: string;
  description: string | null;
  event_date: string;
  event_end_date: string | null;
  /** IANA zone the event times are in; null for legacy events (render local). */
  timezone: string | null;
  /**
   * Organizer-controlled matching window. Matching only runs between these
   * instants; outside them this event produces no matches by design. Null on
   * either side means that end is open. Present so the UI can say matching has
   * not opened yet instead of showing an empty list, which reads as failure.
   * Only populated by /events/mine.
   */
  /** Master switch. False means the organizer has stopped matching for this event. */
  matching_enabled?: boolean;
  matching_starts_at?: string | null;
  matching_ends_at?: string | null;
  venue: string | null;
  city: string | null;
  country: string | null;
  logo_url: string | null;
  status: string;
  participant_count: number;
  /** Multi-event: the caller's goals for this event ([] = same as profile).
   *  Only populated by /events/mine. */
  my_goals?: string[] | null;
  /** Per-event intent interview result. Non-null means the caller declared an
   *  objective for THIS event, and my_goals is [] because the interview
   *  supersedes the chips. Check this BEFORE falling back to "same as profile",
   *  or an event with a declared intent reads as if nothing was set.
   *  Only populated by /events/mine. */
  my_intent_goal?: string | null;
  my_intent_summary?: string | null;
}

export interface EventDetail extends EventItem {
  joined: boolean;
  my_goals: string[] | null;
  my_event_matches: number;
}

export interface EventMatch {
  match_id: string;
  score: number;
  status: string;
  match_reason: string;
  goal_complementary: boolean;
  event_name: string;
  other_user_id: string;
  other_first_name: string;
  other_last_name: string;
  other_photo: string | null;
  other_objective: string | null;
  // F6: match location (city = physical, region = target-market). null when unknown.
  other_city: string | null;
  other_region: string | null;
  other_goals: string[];
}

export interface EventBadge {
  match_id: string;
  event_id: string;
  event_name: string;
  goal_complementary: boolean;
}

export const VALID_GOALS = [
  { value: 'find_investors', label: 'Find investors or raise funding' },
  { value: 'find_cofounders', label: 'Find co-founders or technical talent' },
  { value: 'find_customers', label: 'Find customers or business partnerships' },
  { value: 'find_jobs', label: 'Find job opportunities' },
  { value: 'network_general', label: 'Learn and network generally' },
];

/**
 * Per-event intent interview. Used when an already-onboarded user says they are
 * at this event for something different: the 5 goals above express only 5 of the
 * 13 objectives the matcher understands and carry no supporting detail, so the
 * interview asks the objective's own questions instead. The user's global
 * profile is untouched either way.
 */
export interface EventIntentStart {
  session_id: string;
  greeting: string;
  progress_percent: number;
}

export interface EventIntentTurn {
  session_id: string;
  ai_response: string;
  objective: string | null;
  objective_display: string | null;
  extracted_slots: Record<string, unknown>;
  progress_percent: number;
  is_complete: boolean;
}

export interface EventIntentResult {
  event_id: string;
  intent_goal: string | null;
  intent_summary: string | null;
  slots: Record<string, unknown>;
}

export const eventService = {
  async startEventIntent(eventId: string): Promise<EventIntentStart> {
    const { data: res } = await api.post(`/events/${eventId}/intent/start`);
    return res?.result || res;
  },

  async eventIntentChat(
    eventId: string,
    sessionId: string,
    message: string,
  ): Promise<EventIntentTurn> {
    const { data: res } = await api.post(`/events/${eventId}/intent/chat`, {
      session_id: sessionId,
      message,
    });
    return res?.result || res;
  },

  async completeEventIntent(eventId: string, sessionId: string): Promise<EventIntentResult> {
    const { data: res } = await api.post(`/events/${eventId}/intent/complete`, {
      session_id: sessionId,
    });
    return res?.result || res;
  },

  async listEvents(): Promise<EventItem[]> {
    const { data: res } = await api.get('/events');
    return res?.result || res || [];
  },

  /** Multi-event: the events the current user belongs to (event switcher). */
  async myEvents(): Promise<EventItem[]> {
    const { data: res } = await api.get('/events/mine');
    return res?.result || res || [];
  },

  async getEvent(eventId: string): Promise<EventDetail> {
    const { data: res } = await api.get(`/events/${eventId}`);
    return res?.result || res;
  },

  /** Multi-event Phase B: set or clear my goals for one event (refires matching). */
  async setMyGoals(eventId: string, goals: string[]): Promise<any> {
    const { data: res } = await api.patch(`/events/${eventId}/my-goals`, { goals });
    return res?.result || res;
  },

  async joinEvent(eventId: string, code: string, goals: string[]): Promise<any> {
    const { data: res } = await api.post(`/events/${eventId}/join`, { code, goals });
    return res?.result || res;
  },

  async leaveEvent(eventId: string): Promise<void> {
    await api.delete(`/events/${eventId}/leave`);
  },

  async getEventMatches(eventId: string): Promise<EventMatch[]> {
    const { data: res } = await api.get(`/events/${eventId}/matches`);
    return res?.result || res || [];
  },

  async getMyBadges(): Promise<EventBadge[]> {
    const { data: res } = await api.get('/events/badges/mine');
    return res?.result || res || [];
  },
};
